<template>
  <div class="app">
    <header class="app-header">
      <div class="header-content">
        <h1>🔍 Vérificateur FREP</h1>
        <p>Vérifiez la présence de vos articles dans la base FREP</p>
      </div>
    </header>

    <main class="main-content">
      <!-- Section Upload -->
      <div class="upload-section glass-card">
        <div class="section-title">
          <div class="title-icon">📁</div>
          <h2>Import des données</h2>
        </div>

        <div class="upload-grid">
          <!-- Fichier Articles -->
          <div class="upload-card" :class="{ active: fileContent }">
            <div class="upload-icon">📄</div>
            <h3>Fichier Articles</h3>
            <p>Importez votre fichier TXT contenant les articles</p>
            <input 
              type="file" 
              @change="handleFileUpload" 
              accept=".txt" 
              class="file-input"
              id="file-upload"
            />
            <label for="file-upload" class="upload-button">
              {{ fileContent ? '🔄 Changer le fichier' : '📤 Choisir un fichier' }}
            </label>
            <div v-if="fileContent" class="upload-success">
              ✅ {{ articlesCount }} articles détectés
            </div>
          </div>

          <!-- Données FREP -->
          <div class="upload-card" :class="{ active: frepData }">
            <div class="upload-icon">🏗️</div>
            <h3>Base FREP</h3>
            <p>Chargez les données FREP depuis l'API ou un fichier</p>
             <v-combobox
                  clearable
                  chips
                  label="Marque"
                  :items="store.state.marques"
                  v-model="filtreParams.code_marque"
                  variant="outlined"
                  density="compact"
              ></v-combobox> 
            <!-- <input v-model="marque"  class="form-control form-control-sm" type="text" > -->
            
            <div class="frep-actions">
              <button 
                @click="marque=filtreParams.code_marque.split('-')[0].trim();getArticles()" 
                :disabled="isLoadingFrep"
                class="action-btn primary"
              >
                <span v-if="isLoadingFrep">
                  <div class="spinner"></div>
                  Chargement...
                </span>
                <span v-else>🌐 API FREP</span>
              </button>
              
              <input 
                type="file" 
                @change="handleFrepUpload" 
                accept=".json" 
                class="file-input"
                id="frep-upload"
              />
              <label for="frep-upload" class="action-btn secondary">
                📁 Fichier JSON
              </label>
            </div>

            <div v-if="frepData" class="upload-success">
              ✅ {{ Object.keys(frepData).length }} marques, {{ totalFrepArticles }} articles
            </div>
            
            <div v-if="frepError" class="upload-error">
              ❌ {{ frepError }}
            </div>
          </div>
        </div>

        <!-- Bouton Vérification -->
        <div class="verify-container">
          <button 
            @click="verifyArticles" 
            :disabled="!fileContent || !frepData || isLoadingFrep" 
            class="verify-btn"
            :class="{ ready: fileContent && frepData && !isLoadingFrep }"
          >
            <span class="btn-icon">🔍</span>
            <span class="btn-text">Lancer la vérification</span>
            <span class="btn-arrow">→</span>
          </button>
        </div>
      </div>

      <!-- Résultats -->
      <div v-if="verificationResults" class="results-section">
        <!-- En-tête des résultats -->
        <div class="results-header glass-card">
          <div class="results-title">
            <h2>📊 Résultats de la vérification</h2>
            <p>Analyse complète des articles présents et manquants</p>
          </div>
          
          <div class="stats-grid">
            <div class="stat-item total">
              <div class="stat-icon">📦</div>
              <div class="stat-content">
                <div class="stat-number">{{ totalArticles }}</div>
                <div class="stat-label">Total Articles</div>
              </div>
            </div>
            
            <div class="stat-item found">
              <div class="stat-icon">✅</div>
              <div class="stat-content">
                <div class="stat-number">{{ foundArticles }}</div>
                <div class="stat-label">Articles Trouvés</div>
              </div>
            </div>
            
            <div class="stat-item missing">
              <div class="stat-icon">❌</div>
              <div class="stat-content">
                <div class="stat-number">{{ missingArticles }}</div>
                <div class="stat-label">Articles Manquants</div>
              </div>
            </div>
            
            <div class="stat-item success">
              <div class="stat-icon">📈</div>
              <div class="stat-content">
                <div class="stat-number">{{ successRate }}%</div>
                <div class="stat-label">Taux de Réussite</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation par onglets -->
        <div class="tabs-container glass-card">
          <div class="tabs">
            <button 
              v-for="tab in tabs" 
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="['tab-btn', { active: activeTab === tab.id }]"
            >
              <span class="tab-icon">{{ tab.icon }}</span>
              <span class="tab-text">{{ tab.name }}</span>
              <span v-if="tab.count !== null" class="tab-badge">{{ tab.count }}</span>
            </button>
          </div>
        </div>

        <!-- Contenu des onglets -->
        <div class="tab-content">
          <!-- Articles Manquants -->
          <div v-if="activeTab === 'missing' && missingArticles > 0" class="tab-panel">
            <div class="panel-header">
              <h3>📋 Articles Non Trouvés dans FREP</h3>
              <p>{{ missingArticles }} article(s) nécessitent une attention particulière</p>
            </div>
            <div class="articles-grid">
              <div 
                v-for="article in missingArticlesList" 
                :key="article.key"
                class="article-card missing"
              >
                <div class="article-header">
                  <div class="article-title">
                    <strong>{{ article.REF_AD_INT }}</strong>
                    <span class="article-ean">{{ article.EAN13 }}</span>
                  </div>
                  <span class="status-badge missing">
                    ❌ MANQUANT
                  </span>
                </div>
                
                <div class="article-details">
                  <div class="detail-row">
                    <span class="label">marque:</span>
                    <span class="value">{{ article.code_marque }} - {{ article.marque }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Thème:</span>
                    <span class="value">{{ article.code_theme }} - {{ article.theme }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">couleur:</span>
                    <span class="value">{{ article.LIB_couleur }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">taille:</span>
                    <span class="value">{{ article.taille_AD_INT }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Désignation:</span>
                    <span class="value">{{ article.designation_FR || article.designation_EN }}</span>
                  </div>
                  
                  <div class="detail-row error">
                    <span class="label">Problème:</span>
                    <span class="value">{{ article.verificationDetails }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Articles Trouvés -->
          <div v-if="activeTab === 'found' && foundArticles > 0" class="tab-panel">
            <div class="panel-header">
              <h3>✅ Articles Correctement Référencés</h3>
              <p>{{ foundArticles }} article(s) présents dans la base FREP</p>
            </div>
            <div class="articles-grid">
              <div 
                v-for="article in foundArticlesList" 
                :key="article.key"
                class="article-card found"
              >
                <div class="article-header">
                  <div class="article-title">
                    <strong>{{ article.REF_AD_INT }}</strong>
                    <span class="article-ean">{{ article.EAN13 }}</span>
                  </div>
                  <span class="status-badge found">
                    ✅ TROUVÉ
                  </span>
                </div>
                
                <div class="article-details">
                  <div class="detail-row">
                    <span class="label">marque:</span>
                    <span class="value">{{ article.code_marque }} - {{ article.marque }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Thème:</span>
                    <span class="value">{{ article.code_theme }} - {{ article.theme }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">couleur:</span>
                    <span class="value">{{ article.LIB_couleur }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Désignation:</span>
                    <span class="value">{{ article.designation_FR || article.designation_EN }}</span>
                  </div>
                  
                  <div class="detail-row location">
                    <span class="label">Emplacement FREP:</span>
                    <span class="value">{{ article.frepLocation }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tous les Articles -->
          <div v-if="activeTab === 'all'" class="tab-panel">
            <div class="panel-header">
              <h3>📦 Vue d'Ensemble de Tous les Articles</h3>
              <p>Analyse complète des {{ totalArticles }} articles traités</p>
            </div>
            <div class="articles-grid">
              <div 
                v-for="article in allArticlesList" 
                :key="article.key"
                :class="['article-card', article.status]"
              >
                <div class="article-header">
                  <div class="article-title">
                    <strong>{{ article.REF_AD_INT }}</strong>
                    <span class="article-ean">{{ article.EAN13 }}</span>
                  </div>
                  <span :class="['status-badge', article.status]">
                    {{ article.status === 'found' ? '✅ TROUVÉ' : '❌ MANQUANT' }}
                  </span>
                </div>
                
                <div class="article-details">
                  <div class="detail-row">
                    <span class="label">marque:</span>
                    <span class="value">{{ article.code_marque }} - {{ article.marque }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Thème:</span>
                    <span class="value">{{ article.code_theme }} - {{ article.theme }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">couleur:</span>
                    <span class="value">{{ article.LIB_couleur }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="label">Désignation:</span>
                    <span class="value">{{ article.designation_FR || article.designation_EN }}</span>
                  </div>
                  
                  <div v-if="article.frepLocation" class="detail-row location">
                    <span class="label">Emplacement FREP:</span>
                    <span class="value">{{ article.frepLocation }}</span>
                  </div>
                  
                  <div v-if="article.verificationDetails && article.status === 'missing'" class="detail-row error">
                    <span class="label">Problème:</span>
                    <span class="value">{{ article.verificationDetails }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Structure FREP -->
          <div v-if="activeTab === 'structure'" class="tab-panel">
            <div class="panel-header">
              <h3>🏗️ Structure de la Base FREP</h3>
              <p>Arborescence complète des données chargées</p>
            </div>
            <div class="frep-structure">
              <div class="structure-info">
                <div class="info-row">
                  <span class="label">Nombre de marques:</span>
                  <span class="value">{{ Object.keys(frepData).length }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Nombre total d'articles:</span>
                  <span class="value">{{ totalFrepArticles }}</span>
                </div>
              </div>
              
              <div class="structure-tree">
                <h4>Arborescence FREP :</h4>
                <div class="tree-container">
                  <div 
                    v-for="(marque, marqueCode) in frepData" 
                    :key="marqueCode"
                    class="tree-level"
                  >
                    <div class="tree-node marque">
                      <strong>🏷️ {{ marqueCode }}</strong>
                      <span class="badge">{{ Object.keys(marque).length }} thème(s)</span>
                    </div>
                    
                    <div 
                      v-for="(theme, themeCode) in marque" 
                      :key="themeCode"
                      class="tree-level theme-level"
                    >
                      <div class="tree-node theme">
                        <span>🎨 {{ themeCode }}</span>
                        <span class="badge">{{ Object.keys(theme).length }} référence(s)</span>
                      </div>
                      
                      <div 
                        v-for="(reference, refCode) in theme" 
                        :key="refCode"
                        class="tree-level ref-level"
                      >
                        <div class="tree-node reference">
                          <span>📦 {{ refCode }}</span>
                          <span class="badge">{{ Object.keys(reference).length }} article(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Aucun article manquant -->
          <div v-if="activeTab === 'missing' && missingArticles === 0" class="empty-state">
            <div class="empty-icon">🎉</div>
            <h3>Parfait !</h3>
            <p>Tous vos articles ont été trouvés dans la base FREP</p>
            <div class="success-animation">✨</div>
          </div>
        </div>

        <!-- Actions d'export -->
        <div class="export-section glass-card">
          <h3>📤 Exporter les Résultats</h3>
          <div class="export-actions">
            <button @click="exportResults" class="export-btn">
              <span>📊 Résultats Complets</span>
            </button>
            <button @click="exportMissing" class="export-btn warning" v-if="missingArticles > 0">
              <span>❌ Articles Manquants</span>
            </button>
            <button @click="exportFrepStructure" class="export-btn secondary">
              <span>🏗️ Structure FREP</span>
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import store from './store';

// Réactives
const fileContent = ref(null)
const frepData = ref(null)
const frepError = ref(null)
const isLoadingFrep = ref(false)
const verificationResults = ref(null)
const activeTab = ref('missing')

// Onglets
const tabs = computed(() => [
  { id: 'missing', name: 'Articles Manquants', icon: '❌', count: missingArticles.value },
  { id: 'found', name: 'Articles Trouvés', icon: '✅', count: foundArticles.value },
  { id: 'all', name: 'Tous les Articles', icon: '📦', count: totalArticles.value },
  { id: 'structure', name: 'Structure FREP', icon: '🏗️', count: null }
])

// Computed properties

const articlesCount = computed(() => {
  if (!fileContent.value) return 0
  const lines = fileContent.value.split('\n').filter(line => line.trim())

  return Math.max(0, lines.length - 1)
})
let marque=ref()

const totalFrepArticles = computed(() => {
  if (!frepData.value) return 0
  let count = 0
  for (const marque in frepData.value) {
    for (const theme in frepData.value[marque]) {
      for (const reference in frepData.value[marque][theme]) {
        count += Object.keys(frepData.value[marque][theme][reference]).length
      }
    }
  }
  return count
})

const totalArticles = computed(() => verificationResults.value ? verificationResults.value.length : 0)
const foundArticles = computed(() => verificationResults.value ? verificationResults.value.filter(a => a.status === 'found').length : 0)
const missingArticles = computed(() => verificationResults.value ? verificationResults.value.filter(a => a.status === 'missing').length : 0)
const successRate = computed(() => totalArticles.value > 0 ? Math.round((foundArticles.value / totalArticles.value) * 100) : 0)
const missingArticlesList = computed(() => verificationResults.value ? verificationResults.value.filter(a => a.status === 'missing') : [])
const foundArticlesList = computed(() => verificationResults.value ? verificationResults.value.filter(a => a.status === 'found') : [])
const allArticlesList = computed(() => verificationResults.value || [])

// Methods
const handleFileUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      fileContent.value = e.target.result
    }
    reader.readAsText(file)
  }
}
watch(fileContent,()=>{
  if (fileContent.value){
    const marqueFile = fileContent.value.split('\n')[1].split(';')[2].trim()
    store.commit('addmarque',marqueFile)
    filtreParams.value.code_marque=marqueFile
    getArticles(marqueFile)
  }
  
})
watch(marque,()=>{
    store.commit('addmarque',marque.value)
})
async function getArticles(marque){
    isLoadingFrep.value = true
    let params=
    {   
        marques:marque?`{${marque}}`:`{${filtreParams.value.code_marque.split(" - ").splice(0,1)}}`
    }
    let url=    `https://my.edisac.com/goapi/articlesdeep`
    let config = {
    method: 'get',
    url: url,
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${store.state.user.token}`   // ⬅️ Ajout du token
    },
    params:params
    };
    return   axios.request(config)
    .then((response) => {
        //console.log(response.data);
        frepData.value=response.data.articles
        isLoadingFrep.value = false
    })
    .catch((error) => {
    //console.log(error);
    });
    
}



const handleFrepUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        frepData.value = JSON.parse(e.target.result)
        frepError.value = null
      } catch (error) {
        frepError.value = 'Erreur lors du chargement: ' + error.message
      }
    }
    reader.readAsText(file)
  }
}
let Frep=ref([])

const verifyArticles = () => {
  Frep.value=[]
  store.commit('addDateLivraison',[])
  if (!fileContent.value || !frepData.value) return
  const lines = fileContent.value.split('\n')
  const headers = lines[0].split(';')
  const results = []
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    const values = lines[i].split(';')
    const article = {}
    headers.forEach((header, index) => {
      article[header.trim()] = values[index] ? values[index].trim() : ''
    })
    const verification = findInFrep(article)
    results.push({
      ...article,
      key: `${article.REF_AD_INT}_${article.EAN13}_${i}`,
    // key: `${article.REF_AD_INT}`,
      status: verification.found ? 'found' : 'missing',
      frepLocation: verification.location,
      verificationDetails: verification.details
    })
  }

  verificationResults.value = results
  activeTab.value = 'missing'
  qteDateLivraison(Frep.value)
  Frep.value=convertTabToStructuredJSON(Frep.value)
  store.commit('addArticlesFrep',Frep.value[store.state.marque])
  console.log('storeFripe',store.state.frep.articles);  
}
let qteDateLivraison=(articles)=>{
      for( let date in store.state.dateLivraisons){
        for(let art in articles){
          if(!articles[art][store.state.dateLivraisons[date]]){
            articles[art][store.state.dateLivraisons[date]]={qte:0,export:false}
          }  
        } 
      }
    }
function convertTabToStructuredJSON(tab) {
    const result = {};
    
    tab.forEach(item => {
        const {
            code_marque,
            code_theme,
            reference_y2,
            codebarre
        } = item;
        
        // Initialiser code_marque si n'existe pas
        if (!result[code_marque]) {
            result[code_marque] = {};
        }
        
        // Initialiser code_theme si n'existe pas
        if (!result[code_marque][code_theme]) {
            result[code_marque][code_theme] = {};
        }
        
        // Initialiser reference_y2 si n'existe pas
        if (!result[code_marque][code_theme][reference_y2]) {
            result[code_marque][code_theme][reference_y2] = {};
        }
        
        // Ajouter l'article avec codebarre comme clé
        result[code_marque][code_theme][reference_y2][codebarre] = { ...item };
    });
    
    return result;
}
  let filtreParams=ref({
      etablissement: [] ,
      code_acheteur: null ,
      code_pole: null ,
      code_marque: '731 - ETRIER',
      code_theme:null ,
      code_rayon: null ,
      code_famille: null ,
      code_sousfamille: null ,
      reference:null ,
      collection: null ,
      enStock : null,
      monStock : null
  })
let newCAB=ref('CAB0')
const findInFrep = (article) => {
  const codemarque = article.CODE_MARQUE
  const codeTheme = article.CODE_THEME
  const refFourn = article.REF_AD_INT
  const ean13 = article.EAN13
  const codecouleur = article.CODE_COULEUR
  const couleur = article.LIB_COULEUR
  const taille = article.TAILLE_AD_INT
  let datetab=store.state.dateLivraisons
    if(!datetab.find(el=> el==convertirDate(article.DATE_LIVRAISON))){
        datetab.push(convertirDate(article.DATE_LIVRAISON))
        store.commit('addDateLivraison',datetab)   
    }
    // console.log(refFourn.substring(0,3)+codecouleur);
    
  if (frepData.value[codemarque]?.[codeTheme]?.[refFourn]) {
    for( let cab in frepData.value[codemarque][codeTheme][refFourn]){
      // String(frepData.value[codemarque][codeTheme][refFourn][cab].taille) == String(taille) 
      //       &&
      // console.log(taille);
      // if(taille){
        if(((frepData.value[codemarque][codeTheme][refFourn][cab].taille == taille
              && (frepData.value[codemarque][codeTheme][refFourn][cab].code_couleur == codecouleur 
              || frepData.value[codemarque][codeTheme][refFourn][cab].couleur.toLowerCase() == couleur.toLowerCase()
              || frepData.value[codemarque][codeTheme][refFourn][cab].code_couleur.toLowerCase() == refFourn.substring(0,3).toLowerCase()+codecouleur.toLowerCase())
            )
          ) || frepData.value[codemarque][codeTheme][refFourn][cab].ean13_y2 == ean13 ){
            if(Frep.value.find(el=>el.ean13_y2==ean13)){
              for( let art in Frep.value){
                if (Frep.value[art].ean13_y2==ean13){
                  Frep.value[art][convertirDate(article.DATE_LIVRAISON)]={qte:Number(article.QTE_COMMANDE),export:false}
                  Frep.value[art].repart=parseChaineVersObjet(article.REPARTITION)
                
                    Frep.value[art].code_paysorigine=getPaysOrigine(article.ORIGINEFAB)  || ''
                    Frep.value[art].ean13_y2=article.EAN13 || ''
                    Frep.value[art].collection=article.SAISON || ''
                    Frep.value[art].achat_devise=article.PRIX_ACHAT_$ || article.PRIX_ACHAT_HT || ''
                    Frep.value[art].achat_euro=article.PRIX_ACHAT_HT || ''
                    Frep.value[art].remise_ligne=parseInt(article.REMISE_LIGNE) || 0
                    Frep.value[art].remise_pied1=parseInt(article.REMISE_PIED1) || 0
                    Frep.value[art].remise_pied2=parseInt(article.REMISE_PIED2) || 0
                    Frep.value[art].vte_euro=article.PRIX_PUBLIC_TTC || ''
                    Frep.value[art].prix_promo=article.PRIX_PROMO || ''
                    Frep.value[art].type_promo= article.PRIX_PROMO!=0?'PBG':''
                    Frep.value[art].vte_negoce=article.PRIX_NEGOCE || ''
                    Frep.value[art].code_composition=getComposition(article.COMPOSITION) || ''
                    Frep.value[art].dimension=article.DIMENSIONS_LxHxP || Frep.value[art].dimension
                    Frep.value[art].poidsnet=article.POIDNET_Kg || Frep.value[art].poidsnet
                    // code_rayon=article.CODE_RAYON || '',
                    // code_famille=article.CODE_FAMILLE || '',
                    // code_sousfamille=article.CODE_SOUS_FAMILLE || '',
                    Frep.value[art].designation=article.DESIGNATION_FR || ''
                    // calcprart=getTypeMarchandise(article.TYPE_MARCHANDISE) || ''
                    Frep.value[art].designCompl=getDesignationCompl(article.CODE_RAYON,article.DESIGNATION_COMP) || {code:"",fr:"",en:"",nl:""}
                    Frep.value[art].code_catdouanie=article.CATDOUANIE  || Frep.value[art].code_catdouanie
                    Frep.value[art].statut='old'
                }
              }
            }else{
              Frep.value.push({...frepData.value[codemarque][codeTheme][refFourn][cab],
                [convertirDate(article.DATE_LIVRAISON)]:{qte:Number(article.QTE_COMMANDE),export:false},
                repart:parseChaineVersObjet(article.REPARTITION),
                
                code_paysorigine:getPaysOrigine(article.ORIGINEFAB)  || '',
                ean13_y2:article.EAN13 || '',
                collection:article.SAISON || '',
                achat_devise:article.PRIX_ACHAT_$ || article.PRIX_ACHAT_HT || '',
                achat_euro:article.PRIX_ACHAT_HT || '',
                remise_ligne:parseInt(article.REMISE_LIGNE) || 0,
                remise_pied1:parseInt(article.REMISE_PIED1) || 0,
                remise_pied2:parseInt(article.REMISE_PIED2) || 0,
                vte_euro:article.PRIX_PUBLIC_TTC || '',
                prix_promo:article.PRIX_PROMO || '',
                type_promo: article.PRIX_PROMO!=0?'PBG':'',
                vte_negoce:article.PRIX_NEGOCE || '',
                code_composition:getComposition(article.COMPOSITION) || '',
                dimension:article.DIMENSIONS_LxHxP || frepData.value[codemarque][codeTheme][refFourn][cab].dimension,
                poidsnet:article.POIDNET_Kg || frepData.value[codemarque][codeTheme][refFourn][cab].poidsnet,
                // code_rayon:article.CODE_RAYON || '',
                // code_famille:article.CODE_FAMILLE || '',
                // code_sousfamille:article.CODE_SOUS_FAMILLE || '',
                designation:article.DESIGNATION_FR || '',
                // calcprart:getTypeMarchandise(article.TYPE_MARCHANDISE) || '',
                designCompl:getDesignationCompl(article.CODE_RAYON,article.DESIGNATION_COMP) || {code:"",fr:"",en:"",nl:""},
                code_catdouanie:article.CATDOUANIE  || '',
                statut:'old'
              })
            }
            return{
              found: true,
              location: `${codemarque} → ${codeTheme} → ${refFourn} → ${ean13}`,
              details: 'Article trouvé dans la structure FREP'
            }
          } 
      // }else if((((frepData.value[codemarque][codeTheme][refFourn][cab].code_couleur == codecouleur 
      //         || frepData.value[codemarque][codeTheme][refFourn][cab].couleur.toLowerCase() == couleur.toLowerCase()
      //         || frepData.value[codemarque][codeTheme][refFourn][cab].code_couleur.toLowerCase() == refFourn.substring(0,3).toLowerCase()+codecouleur.toLowerCase())
      //       )
      //     ) || frepData.value[codemarque][codeTheme][refFourn][cab].ean13_y2 == ean13 ){
      //       if(Frep.value.find(el=>el.ean13_y2==ean13)){
      //         for( let art in Frep.value){
      //           if (Frep.value[art].ean13_y2==ean13){
      //             Frep.value[art][convertirDate(article.DATE_LIVRAISON)]={qte:Number(article.QTE_COMMANDE),export:false}
      //           }
      //         }
      //       }else{
      //         Frep.value.push({...frepData.value[codemarque][codeTheme][refFourn][cab],
      //             [convertirDate(article.DATE_LIVRAISON)]:{qte:Number(article.QTE_COMMANDE),export:false},
      //             repart:parseChaineVersObjet(article.repartITION),
      //             STATUT:'old'
      //         })
      //       }
      //       return{
      //           found: true,
      //           location: `${codemarque} → ${codeTheme} → ${refFourn} → ${ean13}`,
      //           details: 'Article trouvé dans la structure FREP'
      //       }
      //     } 
        
    }
  }
  
  let val=Number(newCAB.value.substring(3))
  if(Frep.value.find(el=>el.ean13_y2==ean13)){
    for( let art in Frep.value){
      if (Frep.value[art].ean13_y2==ean13){
        Frep.value[art][convertirDate(article.DATE_LIVRAISON)]={qte:Number(article.QTE_COMMANDE),export:false}
      }
    }
  }else{
    // console.log(article.LIB_couleur.toLowerCase());
    
    newCAB.value=`CAB${val+1}`
    Frep.value.push({...article,
        repart:parseChaineVersObjet(article.REPARTITION),
        acheteur:article.ACHETEUR || '',
        code_fourn:article.CODE_FOURN || '',
        code_marque:article.CODE_MARQUE || '',
        marque:article.MARQUE || '',
        theme:article.THEME || '',
        code_theme:article.CODE_THEME || '',
        theme:article.THEME_AD_INT || '',
        ref_fourn:article.REF_FOURN || '',
        reference_y2:article.REF_AD_INT || '',
        code_couleur:article.REF_AD_INT.substring(0,3)+article.CODE_COULEUR,
        couleur:article.LIB_COULEUR || '',
        taille:article.TAILLE_AD_INT || '',
        code_taille:article.TAILLE_FOURN || '',
        code_paysorigine:getPaysOrigine(article.ORIGINEFAB)  || '',
        ean13_y2:article.EAN13 || '',
        dimension:article.DIMENSIONS_LxHxP || '',
        poidsnet:article.POIDNET_Kg || '',
        collection:article.SAISON || '',
        achat_devise:article.PRIX_ACHAT_$ || article.PRIX_ACHAT_HT || '',
        achat_euro:article.PRIX_ACHAT_HT || '',
        remise_ligne:parseInt(article.REMISE_LIGNE) || 0,
        remise_pied1:parseInt(article.REMISE_PIED1) || 0,
        remise_pied2:parseInt(article.REMISE_PIED2) || 0,
        vte_euro:article.PRIX_PUBLIC_TTC || '',
        prix_promo:article.PRIX_PROMO || '',
        type_promo: article.PRIX_PROMO!=0?'PBG':'',
        vte_negoce:article.PRIX_NEGOCE || '',
        code_composition:getComposition(article.COMPOSITION) || '',
        code_rayon:article.CODE_RAYON || '',
        code_famille:article.CODE_FAMILLE || '',
        code_sousfamille:article.CODE_SOUS_FAMILLE || '',
        designation:article.DESIGNATION_FR || '',
        calcprart:getTypeMarchandise(article.TYPE_MARCHANDISE) || '',
        designCompl:getDesignationCompl(article.CODE_RAYON,article.DESIGNATION_COMP) || {code:"",fr:"",en:"",nl:""},
        code_pole:article.POLE || '',
        ref_fourn:article.NOM_PRODUIT || '',
        codebarre:newCAB.value,
        [convertirDate(article.DATE_LIVRAISON)]:{qte:Number(article.QTE_COMMANDE),export:false},
        ga_comptaarticle:getFamilleCompta(article.CODE_FAMILLE) || '',
        code_catdouanie:article.CATDOUANIE  || '',
        statut:'new'
    })
  }
  
  const details = []
  if (!frepData.value[codemarque]) {
    details.push(`marque "${codemarque}" non trouvée dans FREP`)
  } else if (!frepData.value[codemarque][codeTheme]) {
    details.push(`Thème "${codeTheme}" non trouvé dans la marque "${codemarque}"`)
  } else if (!frepData.value[codemarque][codeTheme][refFourn]) {
    details.push(`Référence "${refFourn}" non trouvée dans ${codemarque}/${codeTheme}`)
  } else {
    details.push(`Code barre "${ean13}" non trouvé dans ${codemarque}/${codeTheme}/${refFourn}`)
  }

  return {
    found: false,
    location: details[0],
    details: details.join(' | ')
  }
}
function supprimerCaracteresSpeciaux(texte, garderAccents = false) {
  let s = String(texte ?? '')
  let text=''
  if (!garderAccents) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  text=s.replace(garderAccents ? /[^\p{L}\p{N}]/gu : /[^A-Za-z0-9]/g, '').toLowerCase()
  // console.log(text);
  return text
}
function getTypeMarchandise(marchandise) {
  const m = marchandise || ''

  if (/\binternationale?\b/i.test(m)) return 'MIN'
  if (/\bnationale?\b/i.test(m))      return 'MNA'
  if (/\beurope\b/i.test(m))          return 'MEU'
  if (/\bimage\b/i.test(m))           return 'IMG'
  return 'MNA'
}
function getDesignationCompl(code_rayon,designation) {
  let desigCompl={
    code:'',
    fr:'',
    en:'',
    nl:''
  }
  let regex = new RegExp(supprimerCaracteresSpeciaux(designation), 'gmi');
  if(code_rayon){
    store.state.designCompl[code_rayon].forEach(el=>{
      if(regex.test(supprimerCaracteresSpeciaux(el.fr)) || regex.test(supprimerCaracteresSpeciaux(el.en))){
        desigCompl= {
          code:el.code,
          fr:el.fr,
          en:el.en ,
          nl:el.nl
        }
        return desigCompl
      }
    })
  }
  return desigCompl
}
// console.log(store.state.countries);
function getPaysOrigine(origine) {    
  let code_origineFab =''
  let regex = new RegExp(supprimerCaracteresSpeciaux(origine), 'gmi');
  store.state.countries.forEach(el=>{
    if(regex.test(supprimerCaracteresSpeciaux(el.libelle)) || regex.test(supprimerCaracteresSpeciaux(el.label))){
      code_origineFab=el.code
      return code_origineFab
    }
  })
  return code_origineFab
}
function getFamilleCompta(code_famille){
  let familleCompta=''
  switch (code_famille){
      case "D1" : familleCompta="EMB";
          break;
      case "D2" : familleCompta="PEN";
          break;
      case "D3" : familleCompta="FAD";
          break;
      case "D4" : familleCompta="REP";
          break;
      case "D5" : familleCompta="PUB";
          break;
      case "D6" : familleCompta="PKG";
          break;
      case "D7" : familleCompta="PTO";
          break;
      case "D8" : familleCompta="VET";
      break;
      default: familleCompta="MAR";
  }
  return familleCompta
}
function getComposition(composition) {
  let compo =''
  let regex = new RegExp(`^${composition}$` , 'gmi');
  store.state.compositions.forEach(el=>{
    if(supprimerCaracteresSpeciaux(composition,true)==supprimerCaracteresSpeciaux(el.libelle,true) 
    ||  supprimerCaracteresSpeciaux(composition)==supprimerCaracteresSpeciaux(el.libelle)
    || supprimerCaracteresSpeciaux(composition)==supprimerCaracteresSpeciaux(el.libelle,true)
    || supprimerCaracteresSpeciaux(composition,true)==supprimerCaracteresSpeciaux(el.libelle)
  ){
      compo=el.code
      return compo
    }
  })
  return compo
}
function excelDateToYYYYMMDD(excelDate) {
  // Excel commence au 1er janvier 1900
  const startDate = new Date(1900, 0, 1);

  // Excel compte le 29 février 1900 (qui n'a jamais existé), donc on retire 1 jour
  const realDate = new Date(startDate.getTime() + (excelDate - 2) * 86400000);

  const year = realDate.getFullYear();
  const month = String(realDate.getMonth() + 1).padStart(2, '0'); // getMonth() est 0-indexé
  const day = String(realDate.getDate()).padStart(2, '0');

  return `${year}${month}${day}`;
}
function convertirDate(dateStr) {
  if(dateStr.length!=8){
     const [jour, mois, annee] = dateStr.split('/');
    const j = jour.padStart(2, '0');
    const m = mois.padStart(2, '0');

    return `${annee}${m}${j}`;
  }else{
    return dateStr
  }
 
}
function parseChaineVersObjet(chaine) {
  const result = {};

  chaine.split(',').forEach(pair => {
    const [key, value] = pair.split(':').map(s => s.trim());
    if (key && value) {
      result[key] = Number(value);
    }
  });

  return result;
}

const exportResults = () => {
  const data = JSON.stringify(verificationResults.value, null, 2)
  downloadFile(data, 'verification_complete.json')
}

const exportMissing = () => {
  const missingData = verificationResults.value.filter(a => a.status === 'missing')
  const data = JSON.stringify(missingData, null, 2)
  downloadFile(data, 'articles_manquants.json')
}

const exportFrepStructure = () => {
  const data = JSON.stringify(frepData.value, null, 2)
  downloadFile(data, 'structure_frep.json')
}

const downloadFile = (content, filename) => {
  const blob = new Blob([content], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
/* Reset et base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.app-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 2rem 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  color: white;
}

.header-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.header-content p {
  font-size: 1.1rem;
  opacity: 0.9;
}

/* Main Content */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

/* Glass Card */
.glass-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
}

/* Section Titles */
.section-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.title-icon {
  font-size: 2rem;
}

.section-title h2 {
  color: white;
  font-size: 1.8rem;
  font-weight: 600;
}

/* Upload Grid */
.upload-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.upload-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
}

.upload-card.active {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.upload-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.upload-card h3 {
  color: white;
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
}

.upload-card p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
}

/* File Input */
.file-input {
  display: none;
}

.upload-button {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 0.9rem;
}

.upload-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* FREP Actions */
.frep-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.action-btn {
  padding: 0.8rem 1.2rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-btn.primary {
  background: #2196F3;
  color: white;
}

.action-btn.secondary {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Upload Status */
.upload-success {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.upload-error {
  background: rgba(244, 67, 54, 0.2);
  color: #f44336;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

/* Verify Button */
.verify-container {
  text-align: center;
}

.verify-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 1.2rem 2.5rem;
  border-radius: 15px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 1rem;
}

.verify-btn.ready {
  background: #4CAF50;
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.3);
}

.verify-btn.ready:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(76, 175, 80, 0.4);
}

.verify-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Results Section */
.results-section {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Results Header */
.results-title h2 {
  color: white;
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
}

.results-title p {
  color: rgba(255, 255, 255, 0.8);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
}

.stat-item {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-5px);
}

.stat-icon {
  font-size: 2rem;
}

.stat-content {
  flex: 1;
}

.stat-number {
  color: white;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem;
  border-radius: 15px;
}

.tab-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.tab-btn.active {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.tab-badge {
  background: rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Tab Content */
.tab-content {
  margin-top: 2rem;
}

.tab-panel {
  animation: fadeIn 0.3s ease;
}

.panel-header {
  text-align: center;
  margin-bottom: 2rem;
}

.panel-header h3 {
  color: white;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.panel-header p {
  color: rgba(255, 255, 255, 0.8);
}

/* Articles Grid */
.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

/* Article Card */
.article-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.article-card.found {
  border-left-color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.article-card.missing {
  border-left-color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}

.article-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.article-title strong {
  color: white;
  font-size: 1.2rem;
  display: block;
  margin-bottom: 0.5rem;
}

.article-ean {
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.status-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.found {
  background: rgba(76, 175, 80, 0.3);
  color: #4CAF50;
}

.status-badge.missing {
  background: rgba(244, 67, 54, 0.3);
  color: #f44336;
}

.article-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  gap: 1rem;
}

.label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 100px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.value {
  color: white;
  font-size: 0.9rem;
  flex: 1;
}

.detail-row.location .value {
  font-family: 'Courier New', monospace;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  border-left: 3px solid #2196F3;
}

.detail-row.error .value {
  color: #ff9800;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: white;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  opacity: 0.8;
  margin-bottom: 2rem;
}

.success-animation {
  font-size: 2rem;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

/* FREP Structure */
.frep-structure {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
}

.structure-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.info-row .label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.info-row .value {
  color: #4CAF50;
  font-weight: 700;
  font-size: 1.1rem;
}

.structure-tree h4 {
  color: white;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.tree-container {
  max-height: 500px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 1.5rem;
}

.tree-level {
  margin-left: 1.5rem;
  border-left: 2px solid rgba(255, 255, 255, 0.2);
  padding-left: 1rem;
}

.tree-node {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  margin: 0.5rem 0;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.tree-node:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(5px);
}

.tree-node.marque {
  border-left: 4px solid #2196F3;
}

.tree-node.theme {
  border-left: 4px solid #9C27B0;
}

.tree-node.reference {
  border-left: 4px solid #4CAF50;
}

.badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Export Section */
.export-section h3 {
  color: white;
  margin-bottom: 1.5rem;
  text-align: center;
}

.export-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.export-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.export-btn.warning {
  background: rgba(244, 67, 54, 0.3);
}

.export-btn.secondary {
  background: rgba(156, 39, 176, 0.3);
}

.export-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* Responsive */
@media (max-width: 768px) {
  .upload-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .articles-grid {
    grid-template-columns: 1fr;
  }
  
  .tabs {
    flex-direction: column;
  }
  
  .frep-actions {
    flex-direction: column;
  }
  
  .export-actions {
    flex-direction: column;
  }
  
  .main-content {
    padding: 1rem;
  }
  
  .header-content h1 {
    font-size: 2rem;
  }
}
</style>